import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Film, Loader, Upload, Pencil, Plus, X } from 'lucide-react';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import BuyCreditsModal from '../components/BuyCreditsModal';

// ── Clip-length presets for reference mode ──────────────────────
const CLIP_LENGTHS = [
  { value: 5, label: '5 sec' },
  { value: 6, label: '6 sec' },
  { value: 8, label: '8 sec' },
  { value: 10, label: '10 sec' },
  { value: 12, label: '12 sec' },
];

// ── Brand-color fields (different from Video Builder) ───────────
const EB_COLOR_FIELDS = [
  { key: 'accent', label: 'Accent' },
  { key: 'infoPanel', label: 'Panel' },
  { key: 'text', label: 'Text' },
  { key: 'upNextPanel', label: 'Up next panel' },
];

const DEFAULT_COLORS = {
  accent: '#c8e020',
  infoPanel: '#101216',
  text: '#ffffff',
  upNextPanel: '#000000',
};

function isLightColor(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 0.299 + g * 0.587 + b * 0.114) > 128;
}

function ExerciseBuilder() {
  const { currentUser, isAdmin } = useAuth();

  // ── Page mode ──────────────────────────────────────────────────
  const [pageMode, setPageMode] = useState('reference'); // 'reference' | 'program' | 'instruction'

  // ── Exercise fields ────────────────────────────────────────────
  const [exerciseName, setExerciseName] = useState('');
  const [rxMode, setRxMode] = useState('reps'); // 'reps' | 'duration'
  const [sets, setSets] = useState('3');
  const [reps, setReps] = useState('12');
  const [duration, setDuration] = useState('30');
  const [instruction, setInstruction] = useState('');
  const INSTRUCTION_MAX = 200; // hard cap so preview never exceeds output capacity
  const [categories, setCategories] = useState([]);
  const [categoryInput, setCategoryInput] = useState('');
  const [cues, setCues] = useState(['']);

  // ── Loop duration (program) / clip length (reference) ──────────
  const [loopMin, setLoopMin] = useState('4');
  const [loopSec, setLoopSec] = useState('40');
  const [clipLength, setClipLength] = useState(8);

  // ── Brand colors (persisted to localStorage) ───────────────────
  const [colors, setColors] = useState(() => {
    const saved = localStorage.getItem('exerciseBuilderColors');
    if (saved) {
      // Merge saved over defaults so any newly-added color key (e.g. upNextPanel)
      // falls back to its default instead of being undefined for older saved data.
      try { return { ...DEFAULT_COLORS, ...JSON.parse(saved) }; } catch { return { ...DEFAULT_COLORS }; }
    }
    return { ...DEFAULT_COLORS };
  });
  const [showBrandColorsModal, setShowBrandColorsModal] = useState(false);
  const [recentColors, setRecentColors] = useState(() => {
    const saved = localStorage.getItem('exerciseBuilderRecentColors');
    if (saved) {
      try { return JSON.parse(saved); } catch { return []; }
    }
    return [];
  });

  // ── Uploads ────────────────────────────────────────────────────
  const [logoFile, setLogoFile] = useState(null);
  const [footageFile, setFootageFile] = useState(null);

  // ── Generation state ───────────────────────────────────────────
  const [generating, setGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');
  const [generationError, setGenerationError] = useState('');
  const [generationSuccess, setGenerationSuccess] = useState(false);

  // ── Credits ────────────────────────────────────────────────────
  const [videoCredits, setVideoCredits] = useState(null);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const [cardLast4, setCardLast4] = useState('');

  // ── Responsive ─────────────────────────────────────────────────
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // ── Layout overflow gate ───────────────────────────────────────
  // The category pill sits at the panel bottom via marginTop:auto. When the
  // name/cue text grows tall enough, it pushes the pill past the panel bottom.
  // That nudge is the signal the output would crowd — so we block Generate.
  const infoPanelRef = useRef(null);
  const pillRowRef = useRef(null);
  const [layoutOverflow, setLayoutOverflow] = useState(false);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Listen for credit balance ──────────────────────────────────
  useEffect(() => {
    if (!currentUser?.uid) return;
    const unsub = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setVideoCredits(data.videoCredits ?? 0);
        setCardLast4(data.savedCard?.last4 || '');
      }
    });
    return unsub;
  }, [currentUser?.uid]);

  // ── Persist colors ─────────────────────────────────────────────
  useEffect(() => { localStorage.setItem('exerciseBuilderColors', JSON.stringify(colors)); }, [colors]);
  useEffect(() => { localStorage.setItem('exerciseBuilderRecentColors', JSON.stringify(recentColors)); }, [recentColors]);

  // ── Preview URLs ───────────────────────────────────────────────
  const logoPreviewUrl = useMemo(() => {
    if (!logoFile) return null;
    return URL.createObjectURL(logoFile);
  }, [logoFile]);
  useEffect(() => { return () => { if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl); }; }, [logoPreviewUrl]);

  const footagePreviewUrl = useMemo(() => {
    if (!footageFile) return null;
    return URL.createObjectURL(footageFile);
  }, [footageFile]);
  useEffect(() => { return () => { if (footagePreviewUrl) URL.revokeObjectURL(footagePreviewUrl); }; }, [footagePreviewUrl]);

  const isProgram = pageMode === 'program';
  const isInstruction = pageMode === 'instruction';

  // ── Measure whether cue/name text has pushed the pill past the panel ──
  useLayoutEffect(() => {
    const measure = () => {
      const panel = infoPanelRef.current;
      const pill = pillRowRef.current;
      // No pill (no category yet) → nothing to crowd; Generate is gated on
      // the category requirement separately, so treat as not-overflowing.
      if (!panel || !pill) { setLayoutOverflow(false); return; }
      // Cues only render in program mode, so only there can text crowd the pill.
      if (!isProgram) { setLayoutOverflow(false); return; }
      const panelBottom = panel.getBoundingClientRect().bottom;
      const pillBottom = pill.getBoundingClientRect().bottom;
      // Pill sits flush at panel bottom until text overflows and pushes it down.
      setLayoutOverflow(pillBottom > panelBottom + 2);
    };
    measure();
    // Re-measure once custom fonts load — cqw sizing changes rendered heights.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }
  }, [exerciseName, cues, categories, rxMode, sets, reps, duration, windowWidth, pageMode, isProgram]);

  // ── Derived values ─────────────────────────────────────────────
  const loopTotal = (parseInt(loopMin) || 0) * 60 + (parseInt(loopSec) || 0);
  const rxText = rxMode === 'reps'
    ? `${sets || '0'} × ${reps || '0'} reps`
    : `${sets || '0'} × ${duration || '0'} sec`;

  const formatDuration = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  // ── Suggested loop duration (for reps: sets × reps × ~2.2s/rep + rest) ─
  const suggestedLoop = useMemo(() => {
    if (rxMode !== 'reps') return parseInt(duration) || 30;
    const s = parseInt(sets) || 3;
    const r = parseInt(reps) || 12;
    const rest = 45;
    const perRep = 2.2;
    return Math.round((s * r * perRep + (s - 1) * rest) / 5) * 5;
  }, [rxMode, sets, reps, duration]);

  const applySuggestedLoop = () => {
    setLoopMin(String(Math.floor(suggestedLoop / 60)));
    setLoopSec(String(suggestedLoop % 60));
  };

  // ── Cue management ─────────────────────────────────────────────
  const updateCue = (index, value) => {
    const next = [...cues];
    next[index] = value;
    setCues(next);
  };
  const removeCue = (index) => {
    setCues(cues.filter((_, i) => i !== index));
  };
  const addCue = () => {
    if (cues.length < 2) setCues([...cues, '']);
  };

  // ── Footage validation (gate BEFORE upload) ───────────────────
  // Reject anything over 20s or above 1080p so oversized files never upload.
  const MAX_FOOTAGE_SECONDS = 20;
  const validateFootage = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      const seconds = video.duration;
      const longer = Math.max(video.videoWidth, video.videoHeight);
      const shorter = Math.min(video.videoWidth, video.videoHeight);
      if (seconds > MAX_FOOTAGE_SECONDS + 0.5) {
        resolve({ ok: false, reason: 'Clips must be 20 seconds or less.' });
      } else if (longer > 1920 || shorter > 1080) {
        resolve({ ok: false, reason: 'Footage must be no greater than 1080p.' });
      } else {
        resolve({ ok: true });
      }
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ ok: false, reason: 'Could not read that video file. Try a different clip.' });
    };
    video.src = url;
  });

  const handleFootageSelect = async (file) => {
    // Always clear the input so re-selecting the same file re-triggers onChange
    const input = document.getElementById('eb-footage-input');
    if (input) input.value = '';
    if (!file) { setFootageFile(null); return; }
    setGenerationError('');
    const result = await validateFootage(file);
    if (!result.ok) {
      setGenerationError(result.reason);
      setFootageFile(null);
      return;
    }
    setFootageFile(file);
  };

  // ── Upload to S3 (same pattern as VideoBuilder) ────────────────
  const uploadFileToS3 = async (file, prefix) => {
    const functions = getFunctions();
    const getUploadUrl = httpsCallable(functions, 'getUploadUrl');
    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${prefix}${timestamp}_${safeName}`;
    const result = await getUploadUrl({ fileName, contentType: file.type });
    const presignedUrl = result.data.uploadUrl;
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.onload = () => { if (xhr.status >= 200 && xhr.status < 300) resolve(); else reject(new Error(`Upload failed: ${xhr.status}`)); };
      xhr.onerror = () => reject(new Error('Upload network error'));
      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
    return result.data.fileName;
  };

  // ── Generate clip ──────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!exerciseName.trim()) { setGenerationError('Please enter an exercise name'); return; }
    if (categories.length === 0) { setGenerationError('Please add at least one category'); return; }
    if (layoutOverflow) { setGenerationError('Cue text is too long and will crowd the category tag on-screen. Shorten it to generate.'); return; }
    if (!logoFile) { setGenerationError('Please upload a logo'); return; }
    if (!footageFile) { setGenerationError('Please upload footage'); return; }

    setGenerating(true); setGenerationError(''); setGenerationSuccess(false);
    try {
      setGenerationStatus('Uploading logo...');
      const logoKey = await uploadFileToS3(logoFile, 'eb_logos/');

      setGenerationStatus('Uploading footage...');
      const footageKey = await uploadFileToS3(footageFile, 'eb_footage/');

      setGenerationStatus('Starting clip generation...');
      const functions = getFunctions();
      const launchExerciseBuilder = httpsCallable(functions, 'launchExerciseBuilder');

      const clipDuration = isProgram ? loopTotal : clipLength;

      const result = await launchExerciseBuilder({
        exerciseName: exerciseName.trim(),
        rxMode,
        sets: parseInt(sets) || 3,
        reps: rxMode === 'reps' ? parseInt(reps) || 12 : null,
        duration: rxMode === 'duration' ? parseInt(duration) || 30 : null,
        details: [],
        instruction: isInstruction ? instruction.trim() : '',
        categories,
        pageMode,
        clipDuration,
        accentColor: colors.accent,
        infoPanelColor: colors.infoPanel,
        textColor: colors.text,
        upNextPanelColor: colors.upNextPanel,
        footageKey,
        logoKey,
      });

      if (result.data.success) {
        setGenerationSuccess(true);
        setGenerationStatus(
          isProgram
            ? 'Clip is being generated! It will appear in My Files when ready.'
            : 'Clip is being generated! It will appear in My Files when ready.'
        );
      } else {
        setGenerationError('Failed to start clip generation');
      }
    } catch (error) {
      console.error('Generation error:', error);
      setGenerationError(error.message || 'Failed to generate clip');
    } finally {
      setGenerating(false);
    }
  };

  const resetForNextBuild = () => {
    setExerciseName('');
    setCues(['']);
    setInstruction('');
    setCategories([]);
    setCategoryInput('');
    setFootageFile(null);
    setLogoFile(null);
    setGenerationSuccess(false);
    setGenerationError('');
    setGenerationStatus('');
  };

  // ── Shared style constants (mirroring VideoBuilder exactly) ────
  const sectionStyle = { padding: '16px 20px', borderBottom: '1px solid #f0f0f0' };
  const sectionLabelStyle = { fontSize: '11px', fontWeight: 500, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' };
  const fieldLabelStyle = { fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' };
  const inputStyle = { width: '100%', fontSize: '13px', padding: '6px 8px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fafafa', color: '#1a1a2e', boxSizing: 'border-box' };

  // ── Preview panel proportions ──────────────────────────────────
  const INFO_PERCENT = 34;
  const INSET = '3.5%';
  // Program mode uses VideoBuilder's exact footage/panel split (identical footage panel size)
  const PROG_FOOTAGE_PERCENT = 66.4;
  const PROG_PANEL_PERCENT = 33.6;

  // ═══════════════════════════════════════════════════════════════
  // MOBILE VIEW (< 1024px)
  // ═══════════════════════════════════════════════════════════════
  if (windowWidth < 1024) {
    return (
      <div style={{ padding: '20px', maxWidth: '500px', margin: '0 auto' }}>
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontSize: '20px', fontWeight: 500, margin: 0, color: '#1a1a2e' }}>Exercise Builder</h1>
          <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 0' }}>Create branded exercise clips for your program sequences</p>
        </div>
        <div style={{ textAlign: 'center', padding: '32px 20px' }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%', background: '#fff3cd',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#856404" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
          </div>
          <h2 style={{ fontSize: '18px', fontWeight: 500, margin: '0 0 8px', color: '#1a1a2e' }}>Desktop recommended</h2>
          <p style={{ fontSize: '14px', color: '#666', lineHeight: 1.5, margin: '0 0 24px' }}>
            The Exercise Builder works best on a larger screen. Use a desktop or laptop for the full building experience.
          </p>
        </div>
        {!isAdmin && (
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #eee', padding: '20px', textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '4px' }}>
              <Film size={18} color="#28a745" />
              <span style={{ fontSize: '28px', fontWeight: 500, color: '#1a1a2e' }}>{videoCredits ?? 0}</span>
            </div>
            <span style={{ fontSize: '14px', color: '#666' }}>credits remaining</span>
          </div>
        )}
        {!isAdmin && (
          <button onClick={() => setShowBuyCreditsModal(true)} style={{ width: '100%', padding: '12px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', marginBottom: '20px' }}>
            Buy credits
          </button>
        )}
        <BuyCreditsModal isOpen={showBuyCreditsModal} onClose={() => setShowBuyCreditsModal(false)} currentBalance={videoCredits} cardLast4={cardLast4} onPurchaseComplete={(newBalance) => setVideoCredits(newBalance)} />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // DESKTOP VIEW
  // ═══════════════════════════════════════════════════════════════
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr 260px', gap: '20px', alignItems: 'start', maxWidth: '1200px' }}>

        {/* ═══════════════════════════════════════════════════════ */}
        {/* LEFT PANEL                                             */}
        {/* ═══════════════════════════════════════════════════════ */}
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #eee', overflow: 'hidden' }}>

          {/* MODE */}
          <div style={sectionStyle}>
            <div style={sectionLabelStyle}>Mode</div>
            <select
              value={pageMode}
              onChange={(e) => setPageMode(e.target.value)}
              style={{ ...inputStyle, fontSize: '12px' }}
            >
              <option value="reference">Reference mode — looping demo</option>
              <option value="program">Program mode — timed sequence</option>
              <option value="instruction">Instruction mode — teaching demo</option>
            </select>
          </div>

          {/* EXERCISE */}
          <div style={sectionStyle}>
            <div style={sectionLabelStyle}>Exercise</div>

            {/* Name */}
            <div style={{ marginBottom: '10px' }}>
              <label style={fieldLabelStyle}>Exercise name</label>
              <input
                type="text"
                value={exerciseName}
                onChange={(e) => setExerciseName(e.target.value)}
                placeholder="e.g. Kettlebell Swing"
                style={inputStyle}
              />
            </div>

            {/* Prescription toggle — hidden in instruction mode */}
            {!isInstruction && (
            <div style={{ marginBottom: '10px' }}>
              <label style={fieldLabelStyle}>Prescription</label>
              <div style={{ display: 'flex', background: '#f2f3f5', borderRadius: '8px', padding: '3px', gap: '3px' }}>
                {['reps', 'duration'].map(m => (
                  <button
                    key={m}
                    onClick={() => setRxMode(m)}
                    style={{
                      flex: 1, border: 'none', background: rxMode === m ? 'white' : 'transparent',
                      cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600,
                      color: rxMode === m ? '#1a1a2e' : '#999', padding: '7px 0', borderRadius: '6px',
                      boxShadow: rxMode === m ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                      transition: 'all 0.12s',
                    }}
                  >
                    {m === 'reps' ? 'Sets × Reps' : 'Duration'}
                  </button>
                ))}
              </div>
            </div>
            )}

            {/* Sets × Reps inputs */}
            {!isInstruction && rxMode === 'reps' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <input type="text" value={sets} onChange={(e) => setSets(e.target.value)} style={{ ...inputStyle, width: '60px', textAlign: 'center' }} />
                <span style={{ fontSize: '15px', color: '#999', fontWeight: 600 }}>×</span>
                <input type="text" value={reps} onChange={(e) => setReps(e.target.value)} style={{ ...inputStyle, width: '60px', textAlign: 'center' }} />
                <span style={{ fontSize: '12px', color: '#999' }}>sets × reps</span>
              </div>
            )}

            {/* Duration input — sets × seconds */}
            {!isInstruction && rxMode === 'duration' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <input type="text" value={sets} onChange={(e) => setSets(e.target.value)} style={{ ...inputStyle, width: '60px', textAlign: 'center' }} />
                <span style={{ fontSize: '15px', color: '#999', fontWeight: 600 }}>×</span>
                <input type="text" value={duration} onChange={(e) => setDuration(e.target.value)} style={{ ...inputStyle, width: '70px', textAlign: 'center' }} />
                <span style={{ fontSize: '12px', color: '#999' }}>sets × seconds</span>
              </div>
            )}

            {/* Instruction field — instruction mode only */}
            {isInstruction && (
              <div style={{ marginBottom: '10px' }}>
                <label style={fieldLabelStyle}>Instruction</label>
                <textarea
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value.slice(0, INSTRUCTION_MAX))}
                  maxLength={INSTRUCTION_MAX}
                  rows={4}
                  placeholder="e.g. Keep your back flat and hinge at the hips. Move slowly and control the weight on the way down."
                  style={{ ...inputStyle, resize: 'none', lineHeight: 1.4 }}
                />
                <div style={{ fontSize: '11px', color: instruction.length >= INSTRUCTION_MAX ? '#E63946' : '#999', textAlign: 'right', marginTop: '2px' }}>
                  {instruction.length}/{INSTRUCTION_MAX}
                </div>
              </div>
            )}

            {/* Category */}
            <div>
              <label style={fieldLabelStyle}>Category <span style={{ color: '#E63946', fontSize: '11px' }}>required</span></label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: categories.length < 1 ? '8px' : '0' }}>
                {categories.map((cat, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    background: '#f0f4e6', border: '1px solid #dfe8c4', borderRadius: '6px',
                    padding: '5px 10px', fontSize: '12px', fontWeight: 500, color: '#5f7020',
                  }}>
                    {cat}
                    <div
                      onClick={() => setCategories(categories.filter((_, idx) => idx !== i))}
                      style={{ cursor: 'pointer', color: '#999', fontSize: '14px', lineHeight: 1, display: 'flex', alignItems: 'center' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#E63946'}
                      onMouseLeave={e => e.currentTarget.style.color = '#999'}
                    >×</div>
                  </div>
                ))}
              </div>
              {categories.length < 1 && (
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input
                    type="text"
                    value={categoryInput}
                    onChange={(e) => setCategoryInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && categoryInput.trim()) {
                        e.preventDefault();
                        setCategories([...categories, categoryInput.trim()]);
                        setCategoryInput('');
                      }
                    }}
                    placeholder="e.g. Lower Body"
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button
                    onClick={() => {
                      if (categoryInput.trim()) {
                        setCategories([...categories, categoryInput.trim()]);
                        setCategoryInput('');
                      }
                    }}
                    disabled={!categoryInput.trim()}
                    style={{
                      padding: '0 12px', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                      background: categoryInput.trim() ? '#E63946' : '#eee',
                      color: categoryInput.trim() ? 'white' : '#bbb',
                      cursor: categoryInput.trim() ? 'pointer' : 'default',
                    }}
                  >Add</button>
                </div>
              )}
              {categories.length === 0 && (
                <div style={{ fontSize: '10.5px', color: '#E63946', marginTop: '4px' }}>Add at least one category</div>
              )}
            </div>
          </div>

          {/* LOOP DURATION (program) / CLIP LENGTH (reference) */}
          {isProgram ? (
            <div style={sectionStyle}>
              <div style={sectionLabelStyle}>Loop duration</div>
              <label style={fieldLabelStyle}>How long should this clip loop before advancing, including rest?</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="text" value={loopMin} onChange={(e) => setLoopMin(e.target.value)} style={{ ...inputStyle, width: '60px', textAlign: 'center' }} />
                <span style={{ fontSize: '12px', color: '#999' }}>min</span>
                <input type="text" value={loopSec} onChange={(e) => setLoopSec(e.target.value)} style={{ ...inputStyle, width: '60px', textAlign: 'center' }} />
                <span style={{ fontSize: '12px', color: '#999' }}>sec</span>
              </div>
              <div style={{ fontSize: '11px', color: '#7a8a4a', background: '#f6f9e8', border: '1px solid #e6efc4', borderRadius: '6px', padding: '8px 10px', marginTop: '8px', lineHeight: 1.5 }}>
                {rxMode === 'reps' ? (
                  <>
                    Est. <strong style={{ color: '#5f7020' }}>{sets || 3} sets × {reps || 12} reps</strong> + ~45s rest between sets ≈ <strong style={{ color: '#5f7020' }}>{formatDuration(suggestedLoop)}</strong>.
                    <span onClick={applySuggestedLoop} style={{ color: '#E63946', cursor: 'pointer', fontWeight: 600, marginLeft: '5px', whiteSpace: 'nowrap' }}>Use this</span>
                  </>
                ) : (
                  <>
                    Duration-based · <strong style={{ color: '#5f7020' }}>one round = {formatDuration(parseInt(duration) || 30)}</strong>.
                    <span onClick={() => { setLoopMin(String(Math.floor((parseInt(duration) || 30) / 60))); setLoopSec(String((parseInt(duration) || 30) % 60)); }} style={{ color: '#E63946', cursor: 'pointer', fontWeight: 600, marginLeft: '5px', whiteSpace: 'nowrap' }}>Loop once ({formatDuration(parseInt(duration) || 30)})</span>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div style={sectionStyle}>
              <div style={sectionLabelStyle}>Clip length</div>
              <label style={fieldLabelStyle}>How long is the looping demo before it repeats?</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <select value={clipLength} onChange={(e) => setClipLength(parseInt(e.target.value))} style={{ ...inputStyle, width: 'auto' }}>
                  {CLIP_LENGTHS.map(cl => <option key={cl.value} value={cl.value}>{cl.label}</option>)}
                </select>
                <span style={{ fontSize: '12px', color: '#999' }}>loops indefinitely in-zone</span>
              </div>
              <div style={{ fontSize: '11px', color: '#7a8a4a', background: '#f6f9e8', border: '1px solid #e6efc4', borderRadius: '6px', padding: '8px 10px', marginTop: '8px', lineHeight: 1.5 }}>
                <strong style={{ color: '#5f7020' }}>8 seconds</strong> covers most movements at a natural pace.
              </div>
            </div>
          )}

          {/* BRAND COLORS */}
          <div style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div style={sectionLabelStyle}>Brand colors</div>
              <div onClick={() => setShowBrandColorsModal(true)} style={{ fontSize: '11px', color: '#E63946', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Pencil size={11} /> Edit
              </div>
            </div>
            {EB_COLOR_FIELDS.map(({ key, label }) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #e0e0e0', background: colors[key], flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', color: '#1a1a2e' }}>{label}</div>
                  <div style={{ fontSize: '11px', color: '#999', fontFamily: 'monospace' }}>{colors[key]}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════ */}
        {/* CENTER — PREVIEW                                       */}
        {/* ═══════════════════════════════════════════════════════ */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>

            {/* Preview hint bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '10px 16px', borderBottom: '1px solid #f0f0f0', fontSize: '11px', color: '#999' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
              {isProgram
                ? <>Preview — the right panel & bottom bar are <strong style={{ color: '#666' }}>added by the player at playback</strong>, not part of this video</>
                : <>Preview — <strong style={{ color: '#666' }}>what you see is what gets burned into the clip</strong></>
              }
            </div>

            {/* ── THE STAGE (16:9 burned preview) ── */}
            <div style={{
              width: '100%', aspectRatio: '16/9', overflow: 'hidden',
              background: colors.infoPanel,
              fontFamily: "'Barlow Condensed', 'Arial Narrow', sans-serif",
              containerType: 'size',
              display: 'flex', flexDirection: 'column',
            }}>
              {/* ── PROGRAM MODE stage: footage-left + up-next panel-right (mirrors Video Builder) ── */}
              {isProgram ? (
                <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
                  {/* FOOTAGE (left) — fills, name + prescription overlaid bottom-left */}
                  <div style={{ width: `${PROG_FOOTAGE_PERCENT}%`, background: '#1a1a1a', position: 'relative', display: 'flex', alignItems: 'flex-end', padding: '3% 2.5%', overflow: 'hidden' }}>
                    {footagePreviewUrl ? (
                      <video src={footagePreviewUrl} preload="metadata" muted style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} onLoadedMetadata={(e) => { e.target.currentTime = 0.1; }} />
                    ) : (
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.15 }}><Film size={36} color="white" /></div>
                    )}
                    {/* Logo top-left */}
                    <div style={{ position: 'absolute', top: '8%', left: '4%', width: '10%', aspectRatio: '1', borderRadius: '50%', overflow: 'hidden', background: logoPreviewUrl ? 'transparent' : 'rgba(255,255,255,0.13)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)' }}>
                      {logoPreviewUrl ? (
                        <img src={logoPreviewUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      ) : (
                        <svg width="52%" height="52%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="3" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
                      )}
                    </div>
                    {/* Name + prescription overlaid bottom-left, bold (matches reference treatment) */}
                    <div style={{ position: 'relative', zIndex: 2 }}>
                      <div style={{ fontWeight: 800, fontSize: '3.4cqw', lineHeight: 1.05, textTransform: 'uppercase', color: colors.text, letterSpacing: '0.02em', textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
                        {exerciseName || 'Exercise Name'}
                      </div>
                      <div style={{ fontWeight: 800, fontSize: '4.2cqw', color: colors.accent, lineHeight: 1, marginTop: '1.4cqh', letterSpacing: '0.02em', textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
                        {rxMode === 'reps'
                          ? `${sets || '0'} × ${reps || '0'} reps`
                          : `${sets || '0'} × ${duration || '0'} sec`
                        }
                      </div>
                    </div>
                  </div>

                  {/* UP-NEXT PANEL (right) — burned background color; up-next content is player-drawn (ghosted) */}
                  <div style={{ width: `${PROG_PANEL_PERCENT}%`, flexShrink: 0, background: colors.upNextPanel, position: 'relative', display: 'flex', flexDirection: 'column', padding: '5%' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', opacity: 0.5 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1cqw' }}>
                        <span style={{ color: colors.accent, fontSize: '3cqw', fontWeight: 800 }}>»</span>
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '2cqw', fontWeight: 700, letterSpacing: '0.15em', color: colors.accent, textTransform: 'uppercase' }}>Up Next</span>
                      </div>
                      <div style={{ fontWeight: 800, fontSize: '3.6cqw', color: '#8894a2', textTransform: 'uppercase', marginTop: '1.6cqh', lineHeight: 1.05 }}>— — —</div>
                    </div>
                    <div style={{ flex: 1, marginTop: '2cqh', border: '1px dashed rgba(255,255,255,0.18)', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
                      <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '1.8cqw', fontStyle: 'italic', color: 'rgba(255,255,255,0.35)', textAlign: 'center', padding: '0 1cqw' }}>set in Program Builder</span>
                    </div>
                  </div>
                </div>
              ) : (
              /* Content row: info panel + inset footage (reference / instruction) */
              <div style={{ flex: 1, display: 'flex', padding: INSET, gap: '3cqw', minHeight: 0 }}>

                {/* INFO PANEL (left) — name + prescription vertically centered, pill anchored bottom */}
                <div ref={infoPanelRef} style={{
                  width: `${INFO_PERCENT}%`, flexShrink: 0,
                  display: 'flex', flexDirection: 'column',
                }}>
                  {/* Name + prescription, vertically centered in the panel */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 0 }}>
                    {/* Exercise name */}
                    <div style={{ fontWeight: 800, fontSize: '5cqw', lineHeight: 1.05, textTransform: 'uppercase', color: colors.text, letterSpacing: '0.02em' }}>
                      {exerciseName || 'Exercise Name'}
                    </div>

                    {/* Prescription (reps/duration) OR Instruction text */}
                    {isInstruction ? (
                      <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500, fontSize: '2.58cqw', color: colors.text, lineHeight: 1.25, marginTop: '2.5cqh', letterSpacing: '0.01em', whiteSpace: 'pre-wrap' }}>
                        {instruction || 'Instruction text'}
                      </div>
                    ) : (
                      <div style={{ fontWeight: 800, fontSize: '6.5cqw', color: colors.accent, lineHeight: 1, marginTop: '3cqh', letterSpacing: '0.02em' }}>
                        {rxMode === 'reps'
                          ? `${sets || '0'} × ${reps || '0'} reps`
                          : `${sets || '0'} × ${duration || '0'} sec`
                        }
                      </div>
                    )}

                    {/* Cues — program mode only (single exercise on screen, room to read) */}
                    {isProgram && cues.filter(c => c.trim()).length > 0 && (
                      <div style={{ marginTop: '3.5cqh', display: 'flex', flexDirection: 'column', gap: '1.5cqh' }}>
                        <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: '2.2cqw', color: colors.accent, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                          Cues
                        </div>
                        {cues.filter(c => c.trim()).slice(0, 2).map((cue, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '1.2cqw' }}>
                            <div style={{ width: '0.6cqw', height: '0.6cqw', borderRadius: '50%', background: colors.accent, flexShrink: 0, marginTop: '1.2cqw' }} />
                            <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500, fontSize: '3cqw', color: '#e0e4e8', lineHeight: 1.15 }}>
                              {cue}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Categories — anchored at panel bottom */}
                  {categories.length > 0 && (
                    <div ref={pillRowRef} style={{ display: 'flex', gap: '1cqw', flexShrink: 0 }}>
                      {categories.slice(0, 2).map((cat, i) => (
                        <div key={i} style={{
                          fontFamily: "'Inter', sans-serif", fontSize: '2cqw', fontWeight: 700,
                          textTransform: 'uppercase', letterSpacing: '0.08em',
                          background: colors.accent, color: colors.infoPanel,
                          padding: '0.9cqh 2cqw', lineHeight: 1,
                        }}>
                          {cat}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* FOOTAGE (right, inset) */}
                <div style={{
                  flex: 1, background: '#1a1a1a', overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  position: 'relative',
                }}>
                  {footagePreviewUrl ? (
                    <video
                      src={footagePreviewUrl}
                      preload="metadata"
                      muted
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                      onLoadedMetadata={(e) => { e.target.currentTime = 0.1; }}
                    />
                  ) : (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.15 }}>
                      <Film size={36} color="white" />
                    </div>
                  )}

                  {/* Logo badge */}
                  <div style={{
                    position: 'absolute', top: '6%', right: '5%', width: '10%', aspectRatio: '1',
                    borderRadius: '50%', overflow: 'hidden',
                    background: logoPreviewUrl ? 'transparent' : 'rgba(255,255,255,0.13)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)',
                  }}>
                    {logoPreviewUrl ? (
                      <img src={logoPreviewUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : (
                      <svg width="52%" height="52%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="3" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
                    )}
                  </div>
                </div>
              </div>
              )}

              {/* ── Ghosted live bar (program mode only) — honest: only this clip's duration is known ─────────── */}
              {isProgram && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '4cqw',
                  padding: '1.2cqh 3cqw', flexShrink: 0,
                  borderTop: `0.4cqh solid ${colors.accent}`,
                  background: 'repeating-linear-gradient(45deg, rgba(16,19,25,0.95), rgba(16,19,25,0.95) 6px, rgba(26,30,38,0.95) 6px, rgba(26,30,38,0.95) 12px)',
                }}>
                  {/* Exercise — position is set by the Program Builder, so dashed here, with segmented progress placeholder */}
                  <div style={{ display: 'flex', flexDirection: 'column', opacity: 0.5 }}>
                    <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '1.2cqw', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#aeb8c4', fontWeight: 600, lineHeight: 1 }}>Exercise</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1.5cqw', marginTop: '0.5cqh' }}>
                      <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: '2.8cqw', color: '#8894a2', lineHeight: 1 }}>— / —</span>
                      <div style={{ width: '10cqw', height: '0.7cqh', borderRadius: '10px', background: 'rgba(255,255,255,0.15)', overflow: 'hidden' }}>
                        <div style={{ width: '0%', height: '100%', background: colors.accent }} />
                      </div>
                    </div>
                  </div>

                  {/* Time Left + Workout Left — grouped on the right, matching the player mockup */}
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4cqw' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', opacity: 0.5 }}>
                      <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '1.2cqw', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#aeb8c4', fontWeight: 600, lineHeight: 1 }}>Time Left</span>
                      <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: '2.8cqw', color: '#cfd7e0', lineHeight: 1, marginTop: '0.5cqh' }}>{formatDuration(loopTotal)}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', opacity: 0.5 }}>
                      <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '1.2cqw', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#aeb8c4', fontWeight: 600, lineHeight: 1 }}>Workout Left</span>
                      <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: '2.8cqw', color: '#8894a2', lineHeight: 1, marginTop: '0.5cqh' }}>—:—</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Stats bar + Build button */}
            <div style={{ padding: '10px 14px', borderTop: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#999' }}>{isProgram ? 'Loop duration' : 'Clip length'}</div>
                <div style={{ fontSize: '14px', fontWeight: 500, color: '#1a1a2e' }}>
                  {isProgram ? formatDuration(loopTotal) : formatDuration(clipLength)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#999' }}>{isInstruction ? 'Instruction' : 'Prescription'}</div>
                <div
                  title={isInstruction && instruction.trim() ? instruction.trim() : ''}
                  style={{ fontSize: '14px', fontWeight: 500, color: '#1a1a2e' }}
                >
                  {isInstruction
                    ? (instruction.trim() ? (instruction.trim().length > 10 ? instruction.trim().slice(0, 10) + '…' : instruction.trim()) : '—')
                    : rxText}
                </div>
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '11px', color: '#999' }}>Category</div>
                <div
                  title={categories.length > 0 ? categories.join(' · ') : ''}
                  style={{ fontSize: '14px', fontWeight: 500, color: '#1a1a2e', maxWidth: '160px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {categories.length > 0 ? categories.join(' · ') : '—'}
                </div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
                {!isAdmin && videoCredits !== null && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Film size={16} color={videoCredits <= 0 ? '#999' : videoCredits <= 3 ? '#e67e22' : '#28a745'} />
                    <span style={{ fontSize: '14px', fontWeight: videoCredits <= 0 ? 400 : 600, color: videoCredits <= 0 ? '#999' : videoCredits <= 3 ? '#e67e22' : '#28a745' }}>
                      {videoCredits} credit{videoCredits !== 1 ? 's' : ''}
                    </span>
                    {videoCredits <= 3 && (
                      <span onClick={() => setShowBuyCreditsModal(true)} style={{ fontSize: '12px', color: '#dc3545', cursor: 'pointer', marginLeft: '2px' }}>
                        {videoCredits <= 0 ? 'Buy' : 'Buy more'}
                      </span>
                    )}
                  </div>
                )}
                {layoutOverflow && (
                  <div style={{ flexBasis: '100%', fontSize: '12px', color: '#c62828', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '5px' }}>
                    ✕ Cue text is too long — it will crowd the category tag. Shorten it to generate.
                  </div>
                )}
                <button
                  onClick={handleGenerate}
                  disabled={generating || layoutOverflow || (!isAdmin && videoCredits <= 0)}
                  style={{
                    padding: '8px 24px', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                    cursor: (generating || layoutOverflow || (!isAdmin && videoCredits <= 0)) ? 'default' : 'pointer',
                    background: (generating || layoutOverflow || (!isAdmin && videoCredits <= 0)) ? '#ccc' : '#E63946', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', whiteSpace: 'nowrap',
                    opacity: ((!isAdmin && videoCredits <= 0) || layoutOverflow) ? 0.5 : 1,
                  }}
                >
                  {generating && <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />}
                  {generating ? 'Generating...' : isProgram ? 'Generate Video' : 'Generate Clip'}
                </button>
              </div>
            </div>
          </div>

          {/* Upload area */}
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #eee', padding: '12px 16px', marginTop: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {/* Logo upload */}
              <div
                style={{
                  position: 'relative', border: `1px ${logoFile ? 'solid' : 'dashed'} ${logoFile ? '#c8e6c9' : '#ccc'}`,
                  borderRadius: '6px', padding: '10px', textAlign: 'center',
                  background: logoFile ? '#f1f8e9' : '#fafafa',
                  cursor: logoFile ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', overflow: 'hidden',
                }}
                onClick={() => { if (!logoFile) document.getElementById('eb-logo-input').click(); }}
              >
                <Upload size={14} color={logoFile ? '#2e7d32' : '#999'} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: '12px', color: logoFile ? '#2e7d32' : '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {logoFile ? logoFile.name : 'Upload Logo'}
                </span>
                <input id="eb-logo-input" type="file" accept="image/png" style={{ display: 'none' }} onChange={(e) => setLogoFile(e.target.files[0] || null)} />
                {logoFile && (
                  <div onClick={(e) => { e.stopPropagation(); setLogoFile(null); }} style={{ position: 'absolute', top: '2px', right: '4px', width: '18px', height: '18px', borderRadius: '50%', background: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '12px', color: '#666', lineHeight: 1 }}>&times;</div>
                )}
              </div>

              {/* Footage upload */}
              <div
                style={{
                  position: 'relative', border: `1px ${footageFile ? 'solid' : 'dashed'} ${footageFile ? '#c8e6c9' : '#ccc'}`,
                  borderRadius: '6px', padding: '10px', textAlign: 'center',
                  background: footageFile ? '#f1f8e9' : '#fafafa',
                  cursor: footageFile ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', overflow: 'hidden',
                }}
                onClick={() => { if (!footageFile) document.getElementById('eb-footage-input').click(); }}
              >
                <Upload size={14} color={footageFile ? '#2e7d32' : '#999'} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: '12px', color: footageFile ? '#2e7d32' : '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {footageFile ? footageFile.name : 'Upload Footage'}
                </span>
                <input id="eb-footage-input" type="file" accept="video/*" style={{ display: 'none' }} onChange={(e) => handleFootageSelect(e.target.files[0] || null)} />
                {footageFile && (
                  <div onClick={(e) => { e.stopPropagation(); setFootageFile(null); }} style={{ position: 'absolute', top: '2px', right: '4px', width: '18px', height: '18px', borderRadius: '50%', background: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '12px', color: '#666', lineHeight: 1 }}>&times;</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════ */}
        {/* RIGHT SIDEBAR                                          */}
        {/* ═══════════════════════════════════════════════════════ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

          {/* Build Status */}
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #eee', padding: '16px 20px' }}>
            <div style={sectionLabelStyle}>Build Status</div>
            {!generating && !generationSuccess && !generationError && (
              <div style={{ fontSize: '13px', color: '#999', lineHeight: 1.5 }}>
                {isProgram
                  ? 'Ready to build. This clip will be added to My Files for use in the Sequence Builder.'
                  : 'Ready to build. This looping clip will be added to My Files — drop it into a zone in the Layout Editor.'
                }
              </div>
            )}
            {generating && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Loader size={16} style={{ animation: 'spin 1s linear infinite', color: '#E63946' }} />
                <div style={{ fontSize: '13px', color: '#1a1a2e' }}>{generationStatus}</div>
              </div>
            )}
            {generationSuccess && (
              <div>
                <div style={{ fontSize: '13px', color: '#2e7d32', lineHeight: 1.5, marginBottom: '10px' }}>✓ {generationStatus}</div>
                <button onClick={resetForNextBuild} style={{ padding: '6px 16px', fontSize: '12px', fontWeight: 500, border: 'none', borderRadius: '6px', background: '#2e7d32', color: 'white', cursor: 'pointer' }}>Build another</button>
              </div>
            )}
            {generationError && (
              <div>
                <div style={{ fontSize: '13px', color: '#c62828', lineHeight: 1.5, marginBottom: '10px' }}>✕ {generationError}</div>
                <button onClick={() => setGenerationError('')} style={{ padding: '6px 16px', fontSize: '12px', fontWeight: 500, border: '1px solid #e0e0e0', borderRadius: '6px', background: 'white', color: '#1a1a2e', cursor: 'pointer' }}>Dismiss</button>
              </div>
            )}
          </div>

          {/* Tips — mode-aware */}
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #eee', padding: '16px 20px' }}>
            <div style={sectionLabelStyle}>Tips</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '12px', color: '#666', lineHeight: 1.5 }}>
              {isInstruction ? (
                <>
                  <div><strong>Instruction mode</strong> — use it to explain <strong>how to do a movement</strong> rather than prescribe sets or reps.</div>
                  <div>Keep instructions concise. There's a <strong>200-character limit</strong> so the text always fits cleanly on the clip.</div>
                  <div>Upload <strong>1080p footage</strong> for best quality — the builder will scale and crop to fit.</div>
                  <div>Logo should be a <strong>PNG with transparent background</strong>. It will be placed as-is on the video.</div>
                </>
              ) : isProgram ? (
                <>
                  <div><strong>Program mode</strong> creates a timed block for use in a sequence. Loop duration should cover <strong>all sets plus rest</strong> — the helper suggests a value from your reps.</div>
                  <div>Upload <strong>1080p footage</strong> for best quality — the builder will scale and crop to fit.</div>
                  <div>Keep footage a <strong>clean loop</strong> (start and end in the same position) so the demo repeats smoothly.</div>
                  <div>Logo should be a <strong>PNG with transparent background</strong>. It will be placed as-is on the video.</div>
                </>
              ) : (
                <>
                  <div><strong>Reference mode</strong> creates a short looping demo. Reference clips loop indefinitely — <strong>8 seconds</strong> covers most movements at a natural pace.</div>
                  <div>Upload <strong>1080p footage</strong> for best quality — the builder will scale and crop to fit.</div>
                  <div>Keep footage a <strong>clean loop</strong> (start and end in the same position) so the demo repeats smoothly.</div>
                  <div>Logo should be a <strong>PNG with transparent background</strong>. It will be placed as-is on the video.</div>
                </>
              )}
            </div>
          </div>

          {/* Credits FAQ (non-admin only) */}
          {!isAdmin && (
            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #eee', padding: '16px 20px' }}>
              <div style={sectionLabelStyle}>Exercise Credits FAQ</div>
              <FAQItem question="What are exercise credits?" answer="Each credit lets you generate one branded exercise clip. Credits are purchased separately from your subscription and never expire." />
              <FAQItem question="How much do credits cost?" answer="Packs start at 5 credits for $5. Larger packs offer a lower per-credit price — 250 credits for $150 ($0.60 each)." />
              <FAQItem question="Do credits expire?" answer="No. Credits never expire. Use them whenever you're ready." />
              <FAQItem question="What do I need to provide?" answer="Your exercise footage and your logo. The builder handles the overlay, formatting, and looping automatically." />
            </div>
          )}
        </div>
      </div>

      {/* ── Brand Colors Modal ─────────────────────────────────── */}
      {showBrandColorsModal && (
        <BrandColorsModal
          colorFields={EB_COLOR_FIELDS}
          currentColors={colors}
          recentColors={recentColors}
          onSave={(newColors) => {
            setColors(newColors);
            const newRecents = [...recentColors];
            Object.values(newColors).forEach(hex => {
              const lower = hex.toLowerCase();
              const idx = newRecents.findIndex(c => c.toLowerCase() === lower);
              if (idx !== -1) newRecents.splice(idx, 1);
              newRecents.unshift(lower);
            });
            setRecentColors(newRecents.slice(0, 6));
            setShowBrandColorsModal(false);
          }}
          onClose={() => setShowBrandColorsModal(false)}
        />
      )}

      {/* ── Buy Credits Modal ──────────────────────────────────── */}
      <BuyCreditsModal
        isOpen={showBuyCreditsModal}
        onClose={() => setShowBuyCreditsModal(false)}
        currentBalance={videoCredits}
        cardLast4={cardLast4}
        onPurchaseComplete={(newBalance) => setVideoCredits(newBalance)}
      />

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Brand Colors Modal (reusable — accepts colorFields prop)
// ═══════════════════════════════════════════════════════════════════
function BrandColorsModal({ colorFields, currentColors, recentColors, onSave, onClose }) {
  const fields = colorFields || EB_COLOR_FIELDS;
  const [draftColors, setDraftColors] = useState({ ...currentColors });
  const [activeKey, setActiveKey] = useState(fields[0].key);
  const modalRef = useRef(null);

  const hasChanges = JSON.stringify(draftColors) !== JSON.stringify(currentColors);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleDraftChange = (key, value) => {
    setDraftColors(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}>
      <div ref={modalRef} style={{ background: 'white', borderRadius: '12px', width: '460px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ fontSize: '15px', fontWeight: 600, color: '#1a1a2e' }}>Brand colors</div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: '18px', color: '#999', lineHeight: 1 }}>&times;</div>
        </div>
        <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: fields.length > 2 ? '1fr 1fr' : '1fr 1fr', gap: '12px' }}>
          {fields.map(({ key, label }) => {
            const isActive = activeKey === key;
            return (
              <div key={key} onClick={() => setActiveKey(key)} style={{
                border: isActive ? '2px solid #E63946' : '1px solid #e0e0e0',
                borderRadius: '10px', padding: isActive ? '11px' : '12px', cursor: 'pointer',
                background: isActive ? '#fef8f8' : 'white', transition: 'border-color 0.15s, background 0.15s',
              }}>
                <div style={{ width: '100%', height: '48px', borderRadius: '6px', background: draftColors[key], border: '1px solid #e0e0e0', marginBottom: '8px' }} />
                <div style={{ fontSize: '12px', fontWeight: 500, color: '#1a1a2e' }}>{label}</div>
                <div style={{ fontSize: '11px', color: '#999', fontFamily: 'monospace' }}>{draftColors[key]}</div>
                {!isActive && <div style={{ fontSize: '10px', color: '#bbb', marginTop: '4px' }}>click to edit</div>}
              </div>
            );
          })}
        </div>
        <div style={{ padding: '0 20px 16px' }}>
          <div style={{ fontSize: '11px', fontWeight: 500, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
            Editing: {fields.find(f => f.key === activeKey)?.label}
          </div>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <HexColorPicker color={draftColors[activeKey]} onChange={(c) => handleDraftChange(activeKey, c)} style={{ width: '180px', height: '140px' }} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ width: '100%', height: '48px', borderRadius: '6px', background: draftColors[activeKey], border: '1px solid #e0e0e0' }} />
              <HexColorInput color={draftColors[activeKey]} onChange={(c) => handleDraftChange(activeKey, c)} prefixed style={{ width: '100%', fontSize: '13px', fontFamily: 'monospace', padding: '6px 8px', border: '1px solid #e0e0e0', borderRadius: '6px', background: '#fafafa', color: '#1a1a2e', boxSizing: 'border-box', outline: 'none' }} />
              {recentColors.length > 0 && (
                <div>
                  <div style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' }}>Recent</div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {recentColors.map((c, i) => (
                      <div key={i} onClick={() => handleDraftChange(activeKey, c)} title={c} style={{ width: '24px', height: '24px', borderRadius: '50%', background: c, border: (draftColors[activeKey] || '').toLowerCase() === c.toLowerCase() ? '2px solid #E63946' : '1px solid #e0e0e0', cursor: 'pointer', flexShrink: 0 }} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '12px 20px', borderTop: '1px solid #f0f0f0' }}>
          <button onClick={onClose} style={{ padding: '8px 20px', fontSize: '13px', fontWeight: 500, border: '1px solid #e0e0e0', borderRadius: '8px', background: 'white', color: '#666', cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => onSave(draftColors)} disabled={!hasChanges} style={{ padding: '8px 20px', fontSize: '13px', fontWeight: 600, border: 'none', borderRadius: '8px', background: hasChanges ? '#E63946' : '#eee', color: hasChanges ? 'white' : '#bbb', cursor: hasChanges ? 'pointer' : 'default' }}>Save colors</button>
        </div>
      </div>
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid #f0f0f0' }}>
      <div onClick={() => setOpen(!open)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', cursor: 'pointer' }}>
        <span style={{ fontSize: '13px', fontWeight: 500, color: '#1a1a2e' }}>{question}</span>
        <span style={{ fontSize: '12px', color: '#999', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
      </div>
      {open && (
        <div style={{ padding: '0 0 12px', fontSize: '12px', color: '#666', lineHeight: 1.5 }}>{answer}</div>
      )}
    </div>
  );
}

export default ExerciseBuilder;